import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import Logo from "@/components/Logo";
import Icon from "@/components/Icon";
import { useAuth } from "@/context/AuthContext";
import { apiError } from "@/lib/api";

function googleLogin() {
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const redirectUrl = window.location.origin + "/app";
  window.location.href = "/api/auth/google/login";
}

const Shell = ({ children }) => (
  <div className="min-h-screen grid lg:grid-cols-2">
    <div className="hidden lg:flex flex-col justify-between p-10 bg-primary text-primary-foreground relative overflow-hidden">
      <div className="absolute inset-0 grid-bg opacity-10" />
      <Link to="/" className="relative" aria-label="QR Code Creator Studio home"><Logo variant="dark-bg" className="h-10" /></Link>
      <div className="relative space-y-4">
        <h2 className="text-4xl font-display font-extrabold leading-tight">Create. Customize.<br />Connect.</h2>
        <p className="text-primary-foreground/80 max-w-sm">The most customizable QR platform for teams — dynamic codes, deep design studio, and employee contact QRs that stay up to date.</p>
        <div className="flex gap-2 pt-2">{["Palette", "RefreshCw", "BarChart3"].map((i) => <div key={i} className="h-10 w-10 rounded-xl bg-white/15 grid place-items-center"><Icon name={i} className="h-5 w-5" /></div>)}</div>
      </div>
      <div className="relative text-xs text-primary-foreground/60">© 2026 QR Code Creator Studio</div>
    </div>
    <div className="flex items-center justify-center p-6 sm:p-10 bg-background">
      <div className="w-full max-w-sm fade-up">{children}</div>
    </div>
  </div>
);

const GoogleBtn = () => (
  <button data-testid="google-login" onClick={googleLogin} className="w-full rounded-xl border border-border py-2.5 text-sm font-semibold hover:bg-accent transition-colors flex items-center justify-center gap-2">
    <svg className="h-4 w-4" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1Z" /><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23Z" /><path fill="#FBBC05" d="M5.84 14.1a6.6 6.6 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88l3.66-2.84Z" /><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84C6.71 7.3 9.14 5.38 12 5.38Z" /></svg>
    Continue with Google
  </button>
);

export function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setLoading(true);
    try { const u = await login(email, password); toast.success("Welcome back!"); const nxt = new URLSearchParams(window.location.search).get("next"); nav(nxt && nxt.startsWith("/invite/") ? nxt : (u?.is_platform_admin ? "/admin" : "/app")); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  return (<Shell>
    <div className="lg:hidden mb-8"><Logo /></div>
    <h1 className="text-2xl font-display font-extrabold">Welcome back</h1>
    <p className="text-sm text-muted-foreground mt-1 mb-6">Log in to your QR Code Creator Studio workspace</p>
    <form onSubmit={submit} className="space-y-3">
      <input data-testid="login-email" type="email" required placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} className="w-full rounded-xl border border-input bg-background px-3.5 py-2.5 text-sm outline-none focus:ring-2 focus:ring-primary/40" />
      <input data-testid="login-password" type="password" required placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} className="w-full rounded-xl border border-input bg-background px-3.5 py-2.5 text-sm outline-none focus:ring-2 focus:ring-primary/40" />
      <div className="text-right"><Link to="/forgot-password" data-testid="forgot-link" className="text-xs font-semibold text-primary">Forgot password?</Link></div>
      <button data-testid="login-submit" disabled={loading} className="w-full rounded-xl bg-primary text-primary-foreground py-2.5 text-sm font-bold hover:opacity-90 disabled:opacity-60">{loading ? "Logging in..." : "Log in"}</button>
    </form>
    <div className="my-4 flex items-center gap-3 text-xs text-muted-foreground"><div className="h-px flex-1 bg-border" />OR<div className="h-px flex-1 bg-border" /></div>
    <GoogleBtn />
    <p className="text-sm text-center text-muted-foreground mt-6">No account? <Link to="/register" data-testid="to-register" className="font-semibold text-primary">Sign up free</Link></p>
  </Shell>);
}

export function Register() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setLoading(true);
    try { await register(form.email, form.password, form.name); toast.success("Account created!"); nav("/app"); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  return (<Shell>
    <div className="lg:hidden mb-8"><Logo /></div>
    <h1 className="text-2xl font-display font-extrabold">Create your workspace</h1>
    <p className="text-sm text-muted-foreground mt-1 mb-6">Start building QR codes in minutes</p>
    <form onSubmit={submit} className="space-y-3">
      <input data-testid="register-name" placeholder="Full name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full rounded-xl border border-input bg-background px-3.5 py-2.5 text-sm outline-none focus:ring-2 focus:ring-primary/40" />
      <input data-testid="register-email" type="email" required placeholder="Work email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full rounded-xl border border-input bg-background px-3.5 py-2.5 text-sm outline-none focus:ring-2 focus:ring-primary/40" />
      <input data-testid="register-password" type="password" required placeholder="Password (min 6 chars)" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="w-full rounded-xl border border-input bg-background px-3.5 py-2.5 text-sm outline-none focus:ring-2 focus:ring-primary/40" />
      <button data-testid="register-submit" disabled={loading} className="w-full rounded-xl bg-primary text-primary-foreground py-2.5 text-sm font-bold hover:opacity-90 disabled:opacity-60">{loading ? "Creating..." : "Create account"}</button>
    </form>
    <div className="my-4 flex items-center gap-3 text-xs text-muted-foreground"><div className="h-px flex-1 bg-border" />OR<div className="h-px flex-1 bg-border" /></div>
    <GoogleBtn />
    <p className="text-sm text-center text-muted-foreground mt-6">Have an account? <Link to="/login" data-testid="to-login" className="font-semibold text-primary">Log in</Link></p>
  </Shell>);
}

export function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    const api = (await import("@/lib/api")).default;
    try { await api.post("/auth/forgot-password", { email }); setSent(true); } catch { setSent(true); }
  };
  return (<Shell>
    <h1 className="text-2xl font-display font-extrabold">Reset password</h1>
    {sent ? (
      <div className="mt-6 rounded-xl border border-border bg-card p-4 text-sm"><Icon name="MailCheck" className="h-6 w-6 text-emerald-500 mb-2" />If an account exists for {email}, a reset link has been generated (check backend logs in this demo).</div>
    ) : (
      <form onSubmit={submit} className="space-y-3 mt-6">
        <input data-testid="forgot-email" type="email" required placeholder="Your email" value={email} onChange={(e) => setEmail(e.target.value)} className="w-full rounded-xl border border-input bg-background px-3.5 py-2.5 text-sm outline-none focus:ring-2 focus:ring-primary/40" />
        <button data-testid="forgot-submit" className="w-full rounded-xl bg-primary text-primary-foreground py-2.5 text-sm font-bold hover:opacity-90">Send reset link</button>
      </form>
    )}
    <p className="text-sm text-center text-muted-foreground mt-6"><Link to="/login" className="font-semibold text-primary">Back to login</Link></p>
  </Shell>);
}

export function ResetPassword() {
  const nav = useNavigate();
  const params = new URLSearchParams(window.location.search);
  const [password, setPassword] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    const api = (await import("@/lib/api")).default;
    try { await api.post("/auth/reset-password", { token: params.get("token"), password }); toast.success("Password updated!"); nav("/login"); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };
  return (<Shell>
    <h1 className="text-2xl font-display font-extrabold">Set new password</h1>
    <form onSubmit={submit} className="space-y-3 mt-6">
      <input data-testid="reset-password" type="password" required placeholder="New password" value={password} onChange={(e) => setPassword(e.target.value)} className="w-full rounded-xl border border-input bg-background px-3.5 py-2.5 text-sm outline-none focus:ring-2 focus:ring-primary/40" />
      <button data-testid="reset-submit" className="w-full rounded-xl bg-primary text-primary-foreground py-2.5 text-sm font-bold hover:opacity-90">Update password</button>
    </form>
  </Shell>);
}
